import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import '../styles/ManageTMPS.scss';
import { useRole } from '../utils/RoleContext';
import { fmt, strip } from '../utils/numberFormat';
import { blurOnEnter } from '../utils/keyboard';


const INCLUSIONS = [
  { id: 'inc_1', label: 'Cost of Sales' },
  { id: 'inc_2', label: 'Operating Expenditure' },
  { id: 'inc_3', label: 'Finance Costs (not included in Operating Expenditure)' },
  { id: 'inc_4', label: 'Administrative Expenses' },
  { id: 'inc_5', label: 'Capital Expenditure (Additions of PPE)' },
  { id: 'inc_6', label: 'Other', hasName: true },
  { id: 'inc_7', label: 'Other', hasName: true },
  { id: 'inc_8', label: 'Other', hasName: true },
];

const EXCLUSIONS = [
  { id: 'exc_1', label: 'Depreciation, Amortisation & Impairments' },
  { id: 'exc_2', label: 'Employee Costs' },
  { id: 'exc_3', label: 'Director Emoluments (not included in Employee Costs)' },
  { id: 'exc_4', label: 'Provision of Bad Debts' },
  { id: 'exc_5', label: 'Levies' },
  { id: 'exc_6', label: 'Fines and Penalties' },
  { id: 'exc_7', label: 'Discounts Allowed' },
  { id: 'exc_8', label: 'Loss on Foreign Exchange'},
  { id: 'exc_9', label: 'Imports'},
  { id: 'exc_10', label: 'Supplier Development Contributions'},
  { id: 'exc_11', label: 'Enterprise Development Contributions'},
  { id: 'exc_12', label: 'Socio-Economic Development Contributions'},
  { id: 'exc_13', label: 'Imports'},
  { id: 'exc_14', label: 'UIF and SDL'},
  { id: 'exc_15', label: 'Other', hasName: true },
  { id: 'exc_16', label: 'Other', hasName: true },
  { id: 'exc_17', label: 'Other', hasName: true },
];

const sectionSum = (fields, values) =>
  fields.reduce((sum, f) => {
    const n = parseFloat(strip(values[f.id] || '0'));
    return sum + (isNaN(n) ? 0 : n);
  }, 0);

const ManageTMPS = () => {
  const navigate = useNavigate();
  const { role } = useRole();
  const canEdit = role =='Consultant';
  const [values, setValues] = useState({});
  const [saveStatus, setSaveStatus] = useState('idle'); // 'idle' | 'saving' | 'saved'
  const [errorModal, setErrorModal] = useState(null);

  useEffect(() => {
    api.get('/core/procurement/tmps/')
      .then((res) => {
        if (res.data?.values) setValues(res.data.values);
      })
      .catch((err) => console.error('Failed to load TMPS values:', err));
  }, []);

  const handleChange = (id, raw) => {
    setValues((prev) => ({ ...prev, [id]: raw }));
  };

  const handleBlur = (id) => {
    const raw = values[id] || '';
    if (!raw) return;
    const n = parseFloat(strip(raw));
    if (!isNaN(n)) setValues((prev) => ({ ...prev, [id]: fmt(n) }));
  };

  const handleFocus = (id) => {
    const val = values[id] || '';
    setValues((prev) => ({ ...prev, [id]: strip(val) }));
  };

  const inclTotal = sectionSum(INCLUSIONS, values);
  const exclTotal = sectionSum(EXCLUSIONS, values);
  const tmps = inclTotal - exclTotal;

  const handleSave = async () => {
    setSaveStatus('saving');
    try {
      await api.post('/core/procurement/tmps/save/', { values, tmps });
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (err) {
      setErrorModal('Failed to save. Please try again.');
      setSaveStatus('idle');
    }
  };

  const renderFields = (fields) =>
    fields.map((field) => (
      <div key={field.id} className="tmps-field">
        <label className="tmps-label" htmlFor={field.id}>
          {field.label}
          {field.hasName && (
            <input
              type="text"
              className="tmps-label-name"
              placeholder="Specify..."
              value={values[`${field.id}_name`] || ''}
              onChange={(e) => handleChange(`${field.id}_name`, e.target.value)}
              onKeyDown={blurOnEnter}
              disabled={!canEdit}
            />
          )}
        </label>
        <div className="tmps-input-wrap">
          <span className="tmps-currency">R</span>
          <input
            id={field.id}
            type="text"
            inputMode="decimal"
            className="tmps-input"
            placeholder="0,00"
            value={values[field.id] || ''}
            onChange={(e) => handleChange(field.id, e.target.value)}
            onKeyDown={blurOnEnter}
            onBlur={() => handleBlur(field.id)}
            onFocus={() => handleFocus(field.id)}
            disabled={!canEdit}
          />
        </div>
      </div>
    ));

  return (
    <div className="tmps-container">
      <div className="tmps-header">
        <button className="tmps-back" onClick={() => navigate('/finance-statutory')}>← Back</button>
        <div>
          <h1>Manage Total Measured Procurement Spend</h1>
          <p>Define inclusions and exclusions used to calculate your Total Measured Procurement Spend</p>
        </div>
      </div>

      <div className="tmps-body">

        {/* Inclusions */}
        <div className="tmps-section-label">TMPS Inclusions</div>
        <div className="tmps-card">
          <div className="tmps-card-top tmps-card-top--green">
            <span>Financial items included in Total Measured Procurement Spend</span>
            <span className="tmps-card-total">Total: R {fmt(inclTotal)}</span>
          </div>
          <div className="tmps-grid">
            {renderFields(INCLUSIONS)}
          </div>
        </div>

        {/* Exclusions */}
        <div className="tmps-section-label">TMPS Exclusions</div>
        <div className="tmps-card">
          <div className="tmps-card-top tmps-card-top--red">
            <span>Financial items deducted from Total Measured Procurement Spend</span>
            <span className="tmps-card-total">Total: R {fmt(exclTotal)}</span>
          </div>
          <div className="tmps-grid">
            {renderFields(EXCLUSIONS)}
          </div>
        </div>

        {/* Summary */}
        <div className="tmps-summary">
          <div className="tmps-summary-row">
            <span>Total Inclusions</span>
            <span className="tmps-summary-green">+ R {fmt(inclTotal)}</span>
          </div>
          <div className="tmps-summary-row">
            <span>Total Exclusions</span>
            <span className="tmps-summary-red">− R {fmt(exclTotal)}</span>
          </div>
          <div className="tmps-summary-divider" />
          <div className="tmps-summary-row tmps-summary-row--total">
            <span>Total Measured Procurement Spend (TMPS)</span>
            <span>R {fmt(tmps)}</span>
          </div>
        </div>

        {/* Actions */}
        <div className="tmps-actions">
          <button className="tmps-btn tmps-btn--primary" onClick={handleSave} disabled={saveStatus === 'saving' || !canEdit}>
            {saveStatus === 'saving' ? 'Saving...' : saveStatus === 'saved' ? 'Saved' : 'Save Values'}
          </button>
          <button className="tmps-btn tmps-btn--secondary" onClick={() => navigate('/finance-statutory')} disabled={saveStatus === 'saving'}>Cancel</button>
        </div>

      </div>

      {errorModal && (
        <div className="tmps-modal-overlay">
          <div className="tmps-modal">
            <h3 className="tmps-modal-title">⚠ Save Failed</h3>
            <p className="tmps-modal-body">{errorModal}</p>
            <div className="tmps-modal-actions">
              <button className="tmps-modal-btn tmps-modal-btn--primary" onClick={() => setErrorModal(null)}>
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageTMPS;
